import React, { memo } from 'react';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { Stack } from '@fiverr-private/layout_components';
import { theme } from '@fiverr-private/theme';
import { Text } from '@fiverr-private/typography';
import { logger } from '@fiverr-private/obs';
import { SellerBadge } from '@fiverr-private/listings';
import {
    FederatedSeller,
    useSellerData,
    useIsSellerOnline,
    NewSellerLevelBadge,
    Location,
    Languages,
} from '@fiverr-private/seller_pages_toolbox';
import { SaveToListButton } from '../SaveToListButton';
import { ShareButton } from '../ShareButton/ShareButton';
import { Avatar } from './Avatar';

import { onlineCircleClass } from './styles.ve.css';

const onRenderError = (originalError) => {
    const err = new Error('Failed while rendering the RolePageHeader Component');
    logger.error(err, {
        cause: originalError,
    });
};

export const RolePageHeader = memo(() => {
    const { username, displayName, sellerLevel, sellerId, isPro } = useSellerData(selectSellerSlice);
    const isOnline = useIsSellerOnline();

    return (
        <ErrorBoundary onError={onRenderError}>
            <Stack
                position="relative"
                gap={{ default: '4', md: '6' }}
                direction={{ default: 'column', md: 'row' }}
                alignItems="flexStart"
                width="100%"
            >
                <Stack
                    position="absolute"
                    right={0}
                    top={0}
                    hidden={{ default: false, lg: true }}
                    gap="2"
                    direction="row"
                >
                    <SaveToListButton size="md" sellerId={sellerId} />
                    <ShareButton size="md" sellerId={sellerId} sellerName={username} />
                </Stack>

                <Stack
                    width={{
                        default: '100%',
                        md: 'auto',
                    }}
                    justifyContent="spaceBetween"
                    alignItems="flexStart"
                    direction="row"
                >
                    <Avatar />
                </Stack>

                <Stack gap="0" direction="column" paddingY={{ default: '0', md: '4' }} alignItems="flexStart">
                    <Stack gap="3" alignItems="center" marginBottom="2">
                        {isOnline && <div className={onlineCircleClass} />}
                        <Text fontWeight="bold" size={{ default: 'b_xxl', md: 'b_lg', lg: 'b_xxl' }}>
                            {displayName}
                        </Text>
                        <Text color="bodySecondary" hidden={{ default: true, md: false }}>{`@${username}`}</Text>
                        {isPro && <SellerBadge badge={SellerBadge.BADGES.PRO} dataTestId={undefined} />}
                    </Stack>
                    <Stack gap="4" marginBottom="2">
                        <Location textSize={theme.bodySizes.b_sm} />
                        <Languages textSize={theme.bodySizes.b_sm} withPrefix />
                    </Stack>
                    <Stack gap="2" justifyContent="center" alignItems="center">
                        <NewSellerLevelBadge sellerLevel={sellerLevel} />
                    </Stack>
                </Stack>
            </Stack>
        </ErrorBoundary>
    );
});

RolePageHeader.displayName = 'RolePageHeader';

function selectSellerSlice(seller: FederatedSeller) {
    return {
        sellerId: seller.user.id,
        username: seller.user.name,
        displayName: seller.user.profile.displayName,
        isPro: seller.isPro,
        sellerLevel: seller.sellerLevel,
    };
}
